import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { PrimeTableComponent } from 'src/app/shared/component/prime-table/prime-table.component';
import { Vehicle, VehicleProperty, getVehicleFields } from 'src/app/vehicles/model/vehicle';
import { DataSource, SortBy } from 'src/app/shared/model/data-source';
import { VehiclesService } from 'src/app/vehicles/service/vehicles.service';
import { Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ValueField } from 'src/app/settings/model/value-field';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { DataSourceCreator } from 'src/app/shared/util/data-source-creator';
import { VehiclePropertiesService } from 'src/app/vehicles/service/vehicle-properties.service';
import { ToastService } from 'src/app/common/service/toast.service';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { GroupService } from 'src/app/management/service/group.service';
import { FormFieldElement, FormFieldType, FormFieldValidator } from 'src/app/shared/model/form-field-element';
import { FieldType } from 'src/app/settings/model/field-type';
import { Group } from 'src/app/management/model/group';
import { Validators } from '@angular/forms';
import { FormEditorComponent } from 'src/app/shared/component/form-editor/form-editor.component';


@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.scss']
})
export class VehiclesComponent implements AfterViewInit {

  loading: boolean = true;
  table: PrimeTableComponent<Vehicle>;
  propertiesFields?: ValueField[];
  formFields: FormFieldElement[];
  groups: Group[];

  dataSource: DataSource;
  @ViewChild('vehicleForm') vehicleForm: FormEditorComponent;
  @ViewChild('table') set setTable(table: PrimeTableComponent<Vehicle>) {
    this.table = table;
    if (this.table && this.dataSource)
      this.initTable();
  }

  constructor(private vehiclesService: VehiclesService, private propertiesService: VehiclePropertiesService, public settings: UserSettingsService,
    private dataSourceCreator: DataSourceCreator, private toast: ToastService, private userProfile: UserProfileService,
    private groupService: GroupService) { }

  ngAfterViewInit(): void {
    this.loadData();
  }

  private initTable(): void {
    setTimeout(() => {
      this.table?.init(this.dataSource, this.vehiclesService, false);
    });
  }

  private loadData(): void {
    this.getProperties().pipe(
      map((propertiesFields: ValueField[]) => {
        propertiesFields.forEach(property => property.title = property.name);
        this.propertiesFields = propertiesFields;

        const dataSourceFields: ValueField[] = [...getVehicleFields(this.settings), ...propertiesFields];
        const dataSource = this.dataSourceCreator.create(Vehicle.getName(), dataSourceFields);
        dataSource.sortBy = [new SortBy('sideNumber', 'asc')];
        this.dataSource = dataSource;
        this.vehiclesService.init(dataSource);

        const formFields = this.createVehicleFormFields();

        if (propertiesFields.length > 0) {
          const propertiesFormFields = this.transformVehiclePropertiesToFormFields(propertiesFields);
          formFields.push(...propertiesFormFields);
        }
        return formFields;
      }),
      finalize(() => this.loading = false)
    ).subscribe(
      formFields => setTimeout(() => this.formFields = formFields),
      error => {
        this.toast.showErrorToast('serverConnectionError');
        console.error(error);
      });

  }

  private getProperties(): Observable<ValueField[]> {
    let propertiesObservable: Observable<ValueField[]>;
    if (this.settings.isVehiclePropertiesEnabled() && this.settings.shouldAllowVehiclePropertiesValuesEditing()) {
      propertiesObservable = this.propertiesService.sendFetchRequest()
        .pipe(
          map(response => response.data || []),
          tap(fields => fields.forEach(field => field.visible = true))
        );
    } else {
      propertiesObservable = of([]);
    }
    return propertiesObservable;
  }

  private createVehicleFormFields(): FormFieldElement[] {
    return [
      {
        name: 'id',
        fieldType: FormFieldType.HIDDEN_INPUT,
        pk: true
      },
      {
        name: 'sideNumber',
        fieldType: FormFieldType.INPUT,
        formFieldType: 'text',
        disabledOnEdit: true,
        maxLength: 20,
        validators: [
          {
            validator: Validators.required,
            validatorErrorName: 'required',
            errorKey: 'required'
          },
          {
            validator: Validators.maxLength(20),
            validatorErrorName: 'maxlength',
            errorKey: 'maxLength',
            errorParams: { value: 20 }
          }
        ]
      }
    ];
  }

  saveVehicle(values: any): void {
    const [vehicleValues, vehicleProperties] = this.getVehicleAndPropertiesValues(values);

    let requestObservable: Observable<any>;
    if (vehicleValues.id)
      requestObservable = this.vehiclesService.updateVehicle(vehicleValues);
    else
      requestObservable = this.vehiclesService.addVehicle(vehicleValues);

    if (Object.keys(vehicleProperties).length) {
      requestObservable = requestObservable.pipe(
        map(vehicle => {
          const propertiesChanged: any = {};
          Object.keys(vehicleProperties).forEach(key => {
            if (vehicleProperties[key] != vehicle.properties[key])
              propertiesChanged[key] = vehicleProperties[key];
          });
          if (Object.keys(propertiesChanged).length) {
            propertiesChanged.veh_id = vehicle.id;
            return propertiesChanged;
          } else {
            return null;
          }
        }),
        switchMap(propertiesChanged => {
          if (propertiesChanged)
            return this.vehiclesService.updateVehicleProperties(propertiesChanged);
          return of(true);
        })
      );
    }

    requestObservable.subscribe(
      () => {
        this.refreshData();
      }, error => {
        if (error.errors && Object.keys(error.errors).length) {
          Object.keys(error.errors).forEach(key => {
            this.toast.showErrorToast(error.errors[key]);
          });
        } else {
          this.toast.showErrorToast('vehicleSaveError');
          console.error(error);
        }
      }
    );
  }

  private refreshData(): void {
    this.vehicleForm.resetForm();
    this.table.refreshData();
  }

  private getVehicleAndPropertiesValues(values: any): [any, any] {
    let vehicleValues: any = {};
    const vehicleProperties: any = {};
    if (this.propertiesFields?.length) {
      Object.keys(values).forEach(key => {
        if (getVehicleFields(this.settings).findIndex(field => field.name == key) >= 0 || key == 'groups')
          vehicleValues[key] = values[key];
        else
          vehicleProperties[key] = values[key];
      });
    } else {
      vehicleValues = values;
    }
    return [vehicleValues, vehicleProperties];
  }

  private transformVehiclePropertiesToFormFields(properties: ValueField[]): FormFieldElement[] {
    return properties.map(property => {
      let type: FormFieldType = FormFieldType.INPUT;
      let formFieldType: string;
      let min: number;
      let max: number;
      let maxLength: number;
      let step: number;
      const validators: FormFieldValidator[] = [];
      switch (property.type) {
        case FieldType.BOOLEAN:
          type = FormFieldType.CHECKBOX;
          break;
        case FieldType.INTEGER:
          formFieldType = 'number';
          min = -1000000;
          max = 1000000;
          validators.push(...[
            {
              validator: Validators.pattern('^[-]?[0-9]*$'),
              validatorErrorName: 'pattern',
              errorKey: 'valueOnlyInteger'
            },
            {
              validator: Validators.min(-1000000),
              validatorErrorName: 'min',
              errorKey: 'validRange',
              errorParams: { min: -1000000, max: 1000000 }
            },
            {
              validator: Validators.max(1000000),
              validatorErrorName: 'max',
              errorKey: 'validRange',
              errorParams: { min: -1000000, max: 1000000 }
            }
          ]);
          break;
        case FieldType.FLOAT:
          formFieldType = 'number';
          min = -1000000;
          max = 1000000;
          step = 0.01;
          validators.push(...[
            {
              validator: Validators.pattern(/^[-]?([0-9]+(?:[\.][0-9]*)?|\.[0-9]+)$/),
              validatorErrorName: 'pattern',
              errorKey: 'valueOnlyNumeric'
            },
            {
              validator: Validators.min(-1000000),
              validatorErrorName: 'min',
              errorKey: 'validRange',
              errorParams: { min: -1000000, max: 1000000 }
            },
            {
              validator: Validators.max(1000000),
              validatorErrorName: 'max',
              errorKey: 'validRange',
              errorParams: { min: -1000000, max: 1000000 }
            }
          ]);
          break;
        case FieldType.ENUM:
          type = FormFieldType.SELECT;
          break;
        default:
          formFieldType = 'text';
          maxLength = 255;
          validators.push(...[
            {
              validator: Validators.maxLength(255),
              validatorErrorName: 'maxlength',
              errorKey: 'maxLength',
              errorParams: { value: 255 }
            }
          ]);
          break;
      }
      if (property.fieldName == VehicleProperty.MAC_ADDRESS)
        type = FormFieldType.SELECT;
      return {
        name: property.fieldName,
        textName: property.name,
        fieldType: type,
        formFieldType,
        maxLength,
        min,
        max,
        step,
        validators,
        options: property.options
      };
    });
  }

  onRowSelected(vehicle?: Vehicle): void {
    if (vehicle) {
      this.vehicleForm.editObject(vehicle);
    }
  }

}
